<template>
  <div class="fixed inset-0 bg-background/95 backdrop-blur flex items-center justify-center z-50 p-4">
    <div class="bg-surface rounded-2xl w-full max-w-2xl h-[min(90vh,760px)] flex flex-col overflow-hidden">
      <div class="relative px-4 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-surface-hover shrink-0">
        <h2 class="text-2xl sm:text-3xl font-bold text-text-primary text-center">Results</h2>
        <button
          class="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-text-primary"
          aria-label="Close"
          @click="$emit('close')"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- Leaderboard -->
      <div class="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        <div class="space-y-3">
          <div
            v-for="(score, index) in scores"
            :key="score.userId"
            :class="[
              'flex items-center gap-4 p-4 rounded-xl',
              index === 0 ? 'bg-yellow-500/20 border-2 border-yellow-500' :
              index === 1 ? 'bg-gray-400/20' :
              index === 2 ? 'bg-amber-700/20' :
              'bg-background'
            ]"
          >
            <!-- Rank -->
            <div
              :class="[
                'w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg',
                index === 0 ? 'bg-yellow-500 text-text-primary' :
                index === 1 ? 'bg-gray-400 text-text-primary' :
                index === 2 ? 'bg-amber-700 text-text-primary' :
                'bg-surface-hover text-text-secondary'
              ]"
            >
              {{ index + 1 }}
            </div>

            <!-- Player info -->
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-text-primary truncate">
                {{ score.displayName }}
              </p>
              <p class="text-sm text-text-secondary">
                {{ score.accuracy.toFixed(1) }}% accuracy
              </p>
            </div>

            <!-- Score + grade -->
            <div class="flex items-center gap-3 text-right">
              <GradeBadge :grade="getGradeForAccuracy(score.accuracy)" size="md" />
              <div>
                <p class="text-xl font-bold text-text-primary">
                  {{ score.score.toLocaleString() }}
                </p>
                <p class="text-xs text-text-secondary">
                  {{ score.perfectCount }}P / {{ score.goodCount }}G / {{ score.missedCount }}M
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="isHost" class="shrink-0 flex gap-3 sm:gap-4 p-4 sm:p-6 border-t border-surface-hover bg-surface">
        <button
          class="flex-1 py-3 px-6 bg-primary text-text-primary rounded-xl font-semibold hover:bg-primary/90 transition-colors"
          @click="$emit('play-again')"
        >
          Play Again
        </button>
        <button
          class="py-3 px-6 rounded-xl font-semibold transition-colors bg-surface-hover text-text-primary hover:bg-surface-active"
          @click="$emit('back-to-lobby')"
        >
          Back to Lobby
        </button>
      </div>
      <div v-else class="shrink-0 text-center p-4 sm:p-6 border-t border-surface-hover bg-surface">
        <p class="text-text-secondary text-sm">
          Waiting for host to choose next action...
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ParticipantScore } from '@/types/classroom';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import type { Id } from '../../../convex/_generated/dataModel';
import { getGradeForAccuracy } from '@/utils/grade';
import GradeBadge from '@/components/GradeBadge.vue';

defineProps<{
  scores: ParticipantScore[];
  isHost: boolean;
  musicalPieceId?: Id<'musicalPieces'>;
}>();

defineEmits<{
  (e: 'play-again'): void;
  (e: 'back-to-lobby'): void;
  (e: 'close'): void;
}>();
</script>
